import { isRejectedWithValue, type Middleware } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';

/**
 * Reports to Sentry the API failures that are **not** part of normal use.
 *
 * RTK Query resolves its errors inside the hooks, so a `500` ends up painting
 * an `ErrorState` and dies there: nobody finds out unless the person writes to
 * support. This is the client half of what `SentryGlobalFilter` does in the
 * backend — and it is not redundant with it, because the two hardest cases to
 * diagnose are precisely the ones the server does not see: the request that
 * never arrived (`FETCH_ERROR`: DNS, a dropped network, a proxy cutting in) and
 * the response that arrived broken (`PARSING_ERROR`).
 *
 * What is NOT reported matters as much as what is. A validation 400, a 401 from
 * an expired session, a 404 or the throttler's 429 are correct backend responses
 * to an expected situation: they are the UI working. If they came in, the Sentry
 * project would fill up with "errors" nobody is going to fix and the ones that
 * matter would end up buried.
 *
 * ## The one 404 that IS reportable
 *
 * A 404 saying «no existe ese borrador» is the UI working. A 404 saying
 * «Cannot POST /api/certification-runs/simulation/preview» is not: that is the
 * ROUTER refusing a path, and this client only ever calls paths the generated
 * contract declares. It means the deploy is skewed against the spec, or
 * something below the router swallowed the request.
 *
 * It is exactly what happened on 2026-09-14, and the reason it went a day
 * without anyone knowing: the Simulation's preview posts its XML as base64, the
 * body crossed express body-parser's 100 KiB default, and the parser's rejection
 * skipped Nest's router and came back as an unmatched-route 404. The screen said
 * the endpoint did not exist. Nothing reached Sentry, because a 404 is «the UI
 * working» — and by every other measure it was.
 *
 * The rule is narrow on purpose: the unmatched-route message, not the status.
 * Anything a controller raises deliberately (`NotFoundException`, with its own
 * Spanish text) stays out.
 */

/** The minimal shape of `FetchBaseQueryError` we need here. */
interface RejectedPayload {
  status?: number | string;
  error?: string;
  /** The parsed response body. For this API, an `ApiErrorDto`. */
  data?: unknown;
}

/**
 * Whether a 404's body is Nest's unmatched-route message rather than a
 * controller's own. Nest words it `Cannot <METHOD> <path>`, and no deliberate
 * `NotFoundException` in this backend does.
 */
export function isMissingRoute(data: unknown): boolean {
  if (typeof data !== 'object' || data === null) return false;
  const { message } = data as { message?: unknown };
  return typeof message === 'string' && /^Cannot [A-Z]+ \//.test(message);
}

export function isReportableFailure(payload: unknown): boolean {
  if (typeof payload !== 'object' || payload === null) return false;
  const { status, data } = payload as RejectedPayload;
  if (typeof status === 'number') {
    // The server failed: it is a defect of ours by definition.
    if (status >= 500) return true;
    // A path the router does not know, asked for by a client generated from the
    // contract. See the note above.
    return status === 404 && isMissingRoute(data);
  }
  // With no numeric status there was no HTTP response to interpret.
  return status === 'FETCH_ERROR' || status === 'PARSING_ERROR' || status === 'TIMEOUT_ERROR';
}

/** `api/executeQuery/rejected` → `executeQuery`. To tag query vs. mutation. */
export function operationOf(actionType: string): string {
  const parts = actionType.split('/');
  return parts.length >= 2 ? (parts[parts.length - 2] ?? actionType) : actionType;
}

interface RejectedMeta {
  arg?: { endpointName?: string; type?: string };
}

export const sentryErrorMiddleware: Middleware = () => (next) => (action) => {
  if (isRejectedWithValue(action) && isReportableFailure(action.payload)) {
    const meta = action.meta as RejectedMeta | undefined;
    const endpoint = meta?.arg?.endpointName ?? 'unknown';
    const payload = action.payload as RejectedPayload;
    Sentry.captureException(
      // A plain RTK Query object is not an `Error` and would reach Sentry as
      // "Non-Error exception captured", with no title and no useful grouping. The
      // message carries endpoint and status so the grouping is by those two.
      new Error(`API ${endpoint} falló con ${String(payload.status)}`),
      {
        tags: {
          api_endpoint: endpoint,
          api_status: String(payload.status),
          redux_operation: operationOf(action.type),
        },
        // The payload goes as context, not as part of the error: it can carry the
        // response's body and that must not enter the grouping.
        contexts: { rtkQuery: { endpoint, status: payload.status, error: payload.error } },
      },
    );
  }
  return next(action);
};
